import React from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { IProtectedScreenValidation } from "../../interfaces/IProtectedScreenValidation";
import "./ProtectedScreenValidationModal.css";

interface ProtectedScreenValidationModalProps {
  isOpen: boolean;
  rejection: IProtectedScreenValidation | null;
  /** Ação recomendada: apenas fecha o modal para o usuário editar o chamado. */
  onReview: () => void;
  /** Envio forçado: reenvia o mesmo request com a flag de uso único. */
  onForceSend: () => void;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const ProtectedScreenValidationModal: React.FC<ProtectedScreenValidationModalProps> = ({
  isOpen,
  rejection,
  onReview,
  onForceSend,
}) => {
  if (!rejection) return null;

  const maxScore = rejection.maxScore > 0 ? rejection.maxScore : 10;
  const minScore = rejection.minScore ? rejection.minScore : 7;
  const score = clamp(Number(rejection.score) || 0, 0, maxScore);
  const percent = Math.round((score / maxScore) * 100);
  const thresholdPercent = clamp(Math.round((minScore / maxScore) * 100), 0, 100);
  const level = score >= minScore ? "ok" : score >= minScore - 3 ? "warn" : "low";
  const missingItems = rejection.missingItems ?? [];

  return (
    <Modal
      className={`${localStorage.getItem("mode") ? localStorage.getItem("mode") : "light"}`}
      isOpen={isOpen}
      toggle={onReview}
      backdrop="static"
    >
      <ModalHeader className="modal-header-style theme-text-color">
        Validação de tela protegida
      </ModalHeader>

      <ModalBody className="modal-body-style psv-modal-body">
        <b style={{ color: "red" }}>
          Este chamado é sobre uma tela protegida e a evidência técnica apresentada não atingiu a
          nota mínima para abertura.
        </b>

        <div className="psv-score-block">
          <div className="psv-score-head">
            <strong className="theme-text-color">Nota da evidência técnica</strong>
            <strong className={`psv-score-value psv-score-value-${level}`}>
              {score}/{maxScore}
            </strong>
          </div>

          <div
            className="psv-score-track"
            role="progressbar"
            aria-label="Nota da evidência técnica"
            aria-valuenow={score}
            aria-valuemin={0}
            aria-valuemax={maxScore}
          >
            <div className={`psv-score-fill psv-score-fill-${level}`} style={{ width: `${percent}%` }} />
            <div
              className="psv-score-threshold"
              style={{ left: `${thresholdPercent}%` }}
              title={`Nota mínima: ${minScore}`}
            />
          </div>

          <small className="psv-score-legend">
            Nota mínima para abertura: {minScore}/{maxScore}
          </small>
        </div>

        {rejection.screenName && (
          <div className="psv-section psv-section-gap">
            <strong className="theme-text-color">Tela protegida identificada</strong>
            <div className="psv-screen-badge">{rejection.screenName}</div>
          </div>
        )}

        {rejection.analysis && (
          <div className="psv-section">
            <strong className="theme-text-color">Análise</strong>
            <p className="psv-analysis theme-text-color">{rejection.analysis}</p>
          </div>
        )}

        {missingItems.length > 0 && (
          <div className="psv-section">
            <strong className="theme-text-color">O que está faltando</strong>
            <ul className="psv-missing-list theme-text-color">
              {missingItems.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        )}
      </ModalBody>

      <ModalFooter className="modal-footer-style">
        <Button color="primary" onClick={onReview} id="btn-review-protected-screen">
          Revisar chamado
        </Button>{" "}
        <Button color="danger" size="sm" onClick={onForceSend} id="btn-force-send-protected-screen">
          Enviar mesmo assim
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ProtectedScreenValidationModal;
